import { Label } from "@/components/ui/label";
import { Building2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

export const PageHeader = ({ eyebrow, title, subtitle, children }) => (
  <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between rise">
    <div>
      {eyebrow && <p className="text-xs font-semibold uppercase tracking-[0.18em] text-teal-700 mb-2">{eyebrow}</p>}
      <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900" data-testid="page-title">{title}</h1>
      {subtitle && <p className="mt-2 text-sm sm:text-base text-slate-500 max-w-2xl">{subtitle}</p>}
    </div>
    {children && <div className="flex flex-wrap gap-2">{children}</div>}
  </div>
);

export const StatCard = ({ label, value, hint, icon: Icon, tone = "teal", testId, delay = 0 }) => {
  const tones = {
    teal: "bg-teal-50 text-teal-700", green: "bg-emerald-50 text-emerald-700", rose: "bg-rose-50 text-rose-600",
    amber: "bg-amber-50 text-amber-600", indigo: "bg-indigo-50 text-indigo-600", slate: "bg-slate-100 text-slate-700",
  };
  return (
    <div className="rise rounded-2xl bg-white border border-slate-200/80 p-5 shadow-sm hover:border-teal-500/40 hover:-translate-y-0.5 transition-[transform,border-color] duration-300"
      style={{ animationDelay: `${delay}ms` }} data-testid={testId}>
      <div className="flex items-start justify-between">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
        {Icon && <span className={`rounded-xl p-2 ${tones[tone]}`}><Icon className="h-4 w-4" /></span>}
      </div>
      <p className="mt-3 text-2xl font-semibold font-display text-slate-900 break-words">{value}</p>
      {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
    </div>
  );
};

export const Panel = ({ title, action, children, className = "", testId }) => (
  <section className={`rise rounded-2xl bg-white border border-slate-200/80 shadow-sm ${className}`} data-testid={testId}>
    {title && (
      <div className="flex items-center justify-between gap-3 px-5 pt-5">
        <h3 className="text-base font-semibold text-slate-900">{title}</h3>
        {action}
      </div>
    )}
    <div className="p-5">{children}</div>
  </section>
);

export const Field = ({ label, children, className = "" }) => (
  <div className={`space-y-1.5 ${className}`}>
    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</Label>
    {children}
  </div>
);

export const Empty = ({ text, testId }) => (
  <div className="py-10 text-center text-sm text-slate-400" data-testid={testId}>{text}</div>
);

export const StatusBadge = ({ status }) => {
  const map = {
    paid: "bg-emerald-50 text-emerald-700 border-emerald-200", partial: "bg-amber-50 text-amber-700 border-amber-200",
    unpaid: "bg-rose-50 text-rose-600 border-rose-200", waiting: "bg-amber-50 text-amber-700 border-amber-200",
    in_consultation: "bg-indigo-50 text-indigo-600 border-indigo-200", completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
    active: "bg-emerald-50 text-emerald-700 border-emerald-200", inactive: "bg-slate-100 text-slate-500 border-slate-200",
  };
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold capitalize ${map[status] || map.inactive}`}>
      {String(status).replace("_", " ")}
    </span>
  );
};

export const NeedClinic = ({ isCeo }) => (
  <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="no-clinic-state">
    <Building2 className="mx-auto h-8 w-8 text-teal-700" />
    <p className="mt-3 font-display text-lg text-slate-900">No clinic yet</p>
    <p className="text-sm text-slate-500">{isCeo ? "Create your first clinic to get started." : "Your account is not linked to a clinic."}</p>
    {isCeo && <Button asChild className="mt-4 rounded-full" data-testid="no-clinic-create-link"><Link to="/clinics">Create a clinic</Link></Button>}
  </div>
);
